import { AlertCircle } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useDeleteRawMaterial,
  useRawMaterialUsage,
  useUpdateRawMaterial,
} from "@/hooks/use-raw-materials";
import { getErrorMessage } from "@/lib/error-message";

import { rawMaterialUomLabel } from "./raw-material-utils";

import type { RawMaterial } from "@artisancode/api-types";

export function RawMaterialDeleteDialog({
  item,
  onOpenChange,
}: {
  item: RawMaterial | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: usage, isLoading } = useRawMaterialUsage(item?.id);
  const { mutate: remove, isPending: isDeleting } = useDeleteRawMaterial();
  const { mutate: update, isPending: isUpdating } = useUpdateRawMaterial();

  const open = Boolean(item);

  function handleDeactivate() {
    if (!item) return;
    update(
      { id: item.id, isActive: false },
      {
        onSuccess: () => {
          toast.success(`Bahan baku "${item.name}" berhasil dinonaktifkan.`);
          onOpenChange(false);
        },
        onError: (err: Error) => {
          toast.error(getErrorMessage(err, "Gagal menonaktifkan bahan baku."));
        },
      },
    );
  }

  function handleDelete() {
    if (!item) return;
    remove(item.id, {
      onSuccess: () => {
        toast.success("Bahan baku berhasil dihapus.");
        onOpenChange(false);
      },
      onError: (err: Error) => {
        toast.error(getErrorMessage(err, "Gagal menghapus bahan baku."));
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {usage?.inUse
              ? "Bahan Baku Tidak Dapat Dihapus"
              : "Hapus Bahan Baku"}
          </DialogTitle>
          <DialogDescription>
            {usage?.inUse
              ? `Bahan baku "${item?.name}" masih memiliki data terkait dan tidak dapat dihapus.`
              : `Apakah Anda yakin ingin menghapus bahan baku "${item?.name}"? Tindakan ini tidak dapat dibatalkan.`}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            Memeriksa pemakaian bahan baku...
          </div>
        ) : usage?.inUse ? (
          <div className="space-y-3 py-2">
            <div className="flex items-start gap-3 rounded-md border border-destructive/20 bg-destructive/10 p-3 text-destructive">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
              <div className="text-sm">
                <p className="font-semibold">Alasan Pencegahan</p>
                <p className="mt-0.5 text-xs text-destructive/90">
                  {usage.reason || "Bahan baku sedang digunakan di sistem."}
                </p>
              </div>
            </div>
            <div className="rounded-md border p-3 text-xs space-y-1 bg-muted/50">
              <p className="font-semibold text-foreground">
                Rincian Penggunaan:
              </p>
              {usage.stockBalance > 0 && (
                <p>
                  • Sisa stok on-hand: {usage.stockBalance}{" "}
                  {item ? (rawMaterialUomLabel(item) ?? "") : ""}
                </p>
              )}
              {usage.movementCount > 0 && (
                <p>
                  • Riwayat pergerakan stok: {usage.movementCount} pergerakan
                </p>
              )}
              {usage.draftExpenseCount > 0 && (
                <p>
                  • Tercantum di draft belanja: {usage.draftExpenseCount}{" "}
                  dokumen
                </p>
              )}
              {usage.recipeCount > 0 && (
                <p>• Digunakan resep aktif: {usage.recipeCount} resep</p>
              )}
            </div>
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting || isUpdating}
          >
            Batal
          </Button>

          {usage?.inUse ? (
            item?.isActive && (
              <Button
                type="button"
                variant="secondary"
                onClick={handleDeactivate}
                disabled={isUpdating}
              >
                {isUpdating ? "Menonaktifkan..." : "Nonaktifkan Saja"}
              </Button>
            )
          ) : (
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isLoading || isDeleting}
            >
              {isDeleting ? "Menghapus..." : "Hapus"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
